"use client";

import { type ReactNode, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { asSentence, joinSeries, tidy } from "@/lib/tool-kit/essay/sentence";
import {
  buildToulminParagraphs,
  missingToulminFields,
  TOULMIN_FRAMES,
  TOULMIN_QUALIFIERS,
  TOULMIN_VERSIONS,
  type ToulminField,
  type ToulminInput,
  type ToulminSentence,
} from "@/lib/tool-kit/essay/toulmin";

/** English strings. A translated content file overrides these through `ui`. */
export const TOULMIN_ARGUMENT_GENERATOR_STRINGS = {
  fields: {
    claim: "Claim",
    qualifier: "Qualifier (optional)",
    grounds: "Grounds (data)",
    warrant: "Warrant",
    backing: "Backing (optional)",
    rebuttal: "Rebuttal (optional)",
  },
  placeholders: {
    claim: "e.g. students should take lecture notes by hand",
    qualifier: "e.g. in most lecture courses",
    grounds:
      "e.g. in experiments, students who typed notes answered conceptual questions less accurately (Mueller & Oppenheimer, 2014)",
    warrant:
      "e.g. putting ideas into your own words builds deeper understanding than copying them",
    backing:
      "e.g. research on the generation effect shows people remember what they produce themselves",
    rebuttal: "e.g. a student needs a laptop for accessibility reasons",
  },
  hints: {
    claim: "The position you want the reader to accept, as a clause.",
    qualifier: "How strongly you make the claim — a word or short phrase.",
    grounds: "The evidence or facts you start from. Include the citation.",
    warrant:
      "The general principle that connects the grounds to the claim — often left unstated in everyday argument.",
    backing:
      "Why the warrant itself should be trusted: research, law, expertise.",
    rebuttal: "The circumstances in which the claim would not hold.",
  },
  qualifierPicks: "Quick qualifiers",
  qualifierSuggestions: TOULMIN_QUALIFIERS,
  tryExample: "Try an example",
  clear: "Clear",
  fieldNames: {
    claim: "a claim",
    qualifier: "a qualifier",
    grounds: "grounds",
    warrant: "a warrant",
    backing: "backing",
    rebuttal: "a rebuttal",
  },
  and: "and",
  missingNotice: "Add {fields} to build the argument.",
  diagramHeading: "Your argument, diagrammed",
  diagramIntro:
    "Grounds lead to the claim because of the warrant, which rests on the backing — unless the rebuttal applies.",
  diagramLabels: {
    claim: "Claim",
    qualifier: "Qualifier",
    grounds: "Grounds",
    warrant: "Warrant",
    backing: "Backing",
    rebuttal: "Rebuttal",
  },
  connectors: {
    so: "so",
    since: "since",
    onAccountOf: "on account of",
    unless: "unless",
  },
  emptyOptional: {
    qualifier: "None — the claim is stated without limits.",
    backing: "Not added. Backing shows why the warrant deserves trust.",
    rebuttal: "Not added. A rebuttal names when the claim would not hold.",
  },
  outlineLine: "{label}: {text}",
  copyOutline: "Copy outline",
  paragraphsHeading: "Written as a paragraph",
  paragraphsIntro:
    "The same argument in three orders — honest templates, no AI. Choose the one that suits your essay and rewrite the joins in your own voice.",
  versionLabel: "Version {n}",
  showLabels: "Show what each sentence does",
  partLabels: {
    grounds: "Grounds",
    claim: "Claim",
    groundsClaim: "Grounds + claim",
    warrant: "Warrant",
    backing: "Backing",
    rebuttal: "Rebuttal",
  },
  capitalsTip:
    "Check capitals on names: the first word of a field is lowercased when it moves mid-sentence, unless you capitalize it elsewhere in your text.",
  copy: "Copy",
  copied: "Copied!",
  frames: TOULMIN_FRAMES,
  example: {
    claim: "students should take lecture notes by hand rather than on a laptop",
    qualifier: "in most lecture courses",
    grounds:
      "in a well-known set of experiments, students who took notes on laptops answered conceptual questions less accurately than students who wrote by hand (Mueller & Oppenheimer, 2014)",
    warrant:
      "note-taking that makes students summarize ideas in their own words builds deeper understanding than note-taking that lets them copy a lecture word for word",
    backing:
      "the same experiments found that laptop users transcribed far more of each lecture verbatim, and research on the generation effect shows that people remember material better when they produce it themselves (Slamecka & Graf, 1978)",
    rebuttal:
      "a student needs a laptop for accessibility reasons, or the course rewards verbatim recall more than conceptual understanding",
  },
} as const;

type Strings = typeof TOULMIN_ARGUMENT_GENERATOR_STRINGS;

const FIELD_ORDER: readonly ToulminField[] = [
  "claim",
  "qualifier",
  "grounds",
  "warrant",
  "backing",
  "rebuttal",
];

const SHORT_FIELDS: ReadonlySet<ToulminField> = new Set(["claim", "qualifier"]);

const EMPTY_INPUT: ToulminInput = {
  claim: "",
  qualifier: "",
  grounds: "",
  warrant: "",
  backing: "",
  rebuttal: "",
};

const BADGE =
  "inline-flex h-fit w-fit rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs";

function Box({
  label,
  text,
  empty,
  emphasis = false,
}: {
  label: string;
  text: string;
  empty: string;
  emphasis?: boolean;
}) {
  const tone = emphasis
    ? "border-primary/40 bg-primary/5"
    : "border-border bg-card";
  return (
    <div className={`rounded-lg border p-3 ${tone}`}>
      <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </p>
      {text === "" ? (
        <p className="mt-1 text-muted-foreground text-sm italic">{empty}</p>
      ) : (
        <p className="mt-1 text-foreground text-sm leading-relaxed">{text}</p>
      )}
    </div>
  );
}

/** A labelled drop from the box above — "since", "on account of", "unless". */
function Drop({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center">
      <span aria-hidden="true" className="h-3 w-px bg-border" />
      <span className="text-muted-foreground text-xs italic">{label}</span>
      <span aria-hidden="true" className="mb-1 h-3 w-px bg-border" />
      <div className="w-full">{children}</div>
    </div>
  );
}

function Diagram({
  input,
  strings,
}: {
  input: ToulminInput;
  strings: Strings;
}) {
  const shown = (field: ToulminField) =>
    tidy(input[field]) === "" ? "" : asSentence(input[field]);
  const labels = strings.diagramLabels;
  const empty = strings.emptyOptional;
  return (
    <figure
      aria-label={strings.diagramHeading}
      className="grid gap-x-4 sm:grid-cols-3"
    >
      <div className="sm:col-start-1 sm:row-start-1">
        <Box empty="" label={labels.grounds} text={shown("grounds")} />
      </div>
      <div className="flex flex-col items-center justify-center py-1 sm:col-start-2 sm:row-start-1 sm:flex-row sm:gap-2">
        <span
          aria-hidden="true"
          className="h-3 w-px bg-border sm:h-px sm:w-auto sm:flex-1"
        />
        <span className="text-muted-foreground text-xs italic">
          {strings.connectors.so}
        </span>
        <span
          aria-hidden="true"
          className="h-3 w-px bg-border sm:h-px sm:w-auto sm:flex-1"
        />
        <span aria-hidden="true" className="text-muted-foreground text-sm">
          <span className="sm:hidden">↓</span>
          <span className="hidden sm:inline">→</span>
        </span>
      </div>
      <div className="space-y-2 sm:col-start-3 sm:row-start-1">
        <Box
          empty={empty.qualifier}
          label={labels.qualifier}
          text={tidy(input.qualifier)}
        />
        <Box emphasis empty="" label={labels.claim} text={shown("claim")} />
      </div>
      <div className="sm:col-start-2 sm:row-start-2">
        <Drop label={strings.connectors.since}>
          <Box empty="" label={labels.warrant} text={shown("warrant")} />
        </Drop>
      </div>
      <div className="sm:col-start-2 sm:row-start-3">
        <Drop label={strings.connectors.onAccountOf}>
          <Box
            empty={empty.backing}
            label={labels.backing}
            text={shown("backing")}
          />
        </Drop>
      </div>
      <div className="sm:col-start-3 sm:row-start-2">
        <Drop label={strings.connectors.unless}>
          <Box
            empty={empty.rebuttal}
            label={labels.rebuttal}
            text={shown("rebuttal")}
          />
        </Drop>
      </div>
    </figure>
  );
}

function outlineText(input: ToulminInput, strings: Strings): string {
  return FIELD_ORDER.filter((field) => tidy(input[field]) !== "")
    .map((field) =>
      t(strings.outlineLine, {
        label: strings.diagramLabels[field],
        text:
          field === "qualifier" ? tidy(input[field]) : asSentence(input[field]),
      })
    )
    .join("\n");
}

function paragraphText(sentences: readonly ToulminSentence[]): string {
  return sentences.map((sentence) => sentence.text).join(" ");
}

function Paragraph({
  sentences,
  showLabels,
  strings,
}: {
  sentences: readonly ToulminSentence[];
  showLabels: boolean;
  strings: Strings;
}) {
  if (!showLabels) {
    return (
      <p className="text-foreground text-sm leading-relaxed">
        {paragraphText(sentences)}
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {sentences.map((sentence) => (
        <li
          className="grid gap-1 sm:grid-cols-[8.5rem_1fr] sm:gap-3"
          key={sentence.part}
        >
          <span className={BADGE}>{strings.partLabels[sentence.part]}</span>
          <span className="text-foreground text-sm leading-relaxed">
            {sentence.text}
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function ToulminArgumentGeneratorWidget() {
  const strings = useToolUi(TOULMIN_ARGUMENT_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<ToulminInput>(EMPTY_INPUT);
  const [showLabels, setShowLabels] = useState(true);

  const missing = missingToulminFields(input);
  const paragraphs =
    missing.length === 0 ? buildToulminParagraphs(input, strings.frames) : [];

  function update(field: ToulminField, value: string) {
    setInput((previous) => ({ ...previous, [field]: value }));
  }

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        {FIELD_ORDER.map((field) => (
          <div className="space-y-2" key={field}>
            {SHORT_FIELDS.has(field) ? (
              <TextField
                hint={strings.hints[field]}
                id={`tag-${field}`}
                label={strings.fields[field]}
                onChange={(value) => update(field, value)}
                placeholder={strings.placeholders[field]}
                value={input[field]}
              />
            ) : (
              <TextAreaField
                hint={strings.hints[field]}
                id={`tag-${field}`}
                label={strings.fields[field]}
                onChange={(value) => update(field, value)}
                placeholder={strings.placeholders[field]}
                prose
                rows={2}
                value={input[field]}
              />
            )}
            {field === "qualifier" ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className={HINT}>{strings.qualifierPicks}</span>
                {strings.qualifierSuggestions.map((suggestion) => (
                  <button
                    className={BUTTON_SMALL}
                    key={suggestion}
                    onClick={() => update("qualifier", suggestion)}
                    type="button"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ))}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput({ ...strings.example })}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY_INPUT)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.diagramHeading}</h2>
        {paragraphs.length === 0 ? (
          <Notice>
            {t(strings.missingNotice, {
              fields: joinSeries(
                missing.map((field) => strings.fieldNames[field]),
                strings.and
              ),
            })}
          </Notice>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="max-w-prose text-muted-foreground text-sm">
                {strings.diagramIntro}
              </p>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyOutline}
                small
                text={outlineText(input, strings)}
              />
            </div>
            <Diagram input={input} strings={strings} />
            <h2 className={WIDGET_H2}>{strings.paragraphsHeading}</h2>
            <p className="text-muted-foreground text-sm">
              {strings.paragraphsIntro}
            </p>
            <Checkbox
              checked={showLabels}
              label={strings.showLabels}
              onChange={setShowLabels}
            />
            {paragraphs.map((sentences, index) => (
              <div
                className={`${RESULT_CARD} space-y-3`}
                key={TOULMIN_VERSIONS[index]}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className={WIDGET_H3}>
                    {t(strings.versionLabel, { n: fmt(index + 1) })}
                  </h3>
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copy}
                    small
                    text={paragraphText(sentences)}
                  />
                </div>
                <Paragraph
                  sentences={sentences}
                  showLabels={showLabels}
                  strings={strings}
                />
              </div>
            ))}
            <p className={HINT}>{strings.capitalsTip}</p>
          </>
        )}
      </section>
    </>
  );
}
